import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { Contact, ContactV2, ContactMemberV2, LabelList, searchContact, Person, StaffLabel } from 'type/contact';

// 模型定义, 该用户相关的操作和数据
type InitialState = {
  /** 常用联系人 */
  contactList: ContactMemberV2[]
  /** 搜索联系人结果 */
  searchContactList: ContactMemberV2[]
  /** 企业通讯录（组织架构） */
  orgContactList: ContactV2[]
  /** 常用+组织架构通讯录 */
  unionContacts: ContactV2 | null
  recommendList: Contact[]
  externalContactList: Contact[] // 废弃
  labelList: LabelList[] // 废弃
  staffLabelList: StaffLabel[] // 废弃
  searchContact: searchContact
  searchHasMore: boolean
  loading: boolean
  labelLoading: boolean // 废弃
};

const initialState = {
  contactList: [],
  searchContactList: [],
  orgContactList: [],
  unionContacts: null,
  recommendList: [],
  externalContactList: [],
  labelList: [],
  staffLabelList: [],
  searchContact: {
    searchId: '',
    userId: '',
    contactPersons: []
  },
  searchHasMore: false,
  loading: false,
  labelLoading: false
} as InitialState;

const contact = createSlice({
  name: 'contact',
  initialState: initialState,
  reducers: {
    setContactList (state, action: PayloadAction<ContactMemberV2[]>) {
      state.contactList = action.payload;
    },
    setSearchContactList (state, action: PayloadAction<ContactMemberV2[]>) {
      state.searchContactList = action.payload;
    },
    setOrgContactList (state, action: PayloadAction<ContactV2[]>) {
      state.orgContactList = action.payload;
    },
    setUnionContacts (state, action: PayloadAction<ContactV2>) {
      state.unionContacts = action.payload;
    },
    setRecommendList (state, action: PayloadAction<Contact[]>) {
      state.recommendList = action.payload;
    },
    setExternalContactList (state, action: PayloadAction<Contact[]>) {
      state.externalContactList = action.payload;
    },
    setLabels (state, action: PayloadAction<LabelList[]>) {
      state.labelList = action.payload;
    },
    setLabelList (state, action: PayloadAction<{labelId: number, contact:Contact[]}>) {
      const contact = action.payload.contact;
      state.labelList = (state.labelList as LabelList[]).map(
        label => label.id === action.payload.labelId ? { ...label, contact } : label
      );
    },
    setStaffLabelList (state, action: PayloadAction<StaffLabel[]>) {
      state.staffLabelList = action.payload;
    },
    setSearchList (state, action: PayloadAction<searchContact>) {
      state.searchHasMore = !!action.payload.contactPersons.length;
      let contactPersons = state.searchContact.contactPersons;
      if (state.searchContact.searchId === action.payload.searchId) {
        contactPersons = contactPersons.concat(action.payload.contactPersons);
      } else {
        contactPersons = action.payload.contactPersons;
      }
      contactPersons = contactPersons.map(p => ({ ...p, staffId: p.personStaffId }));
      state.searchContact = { ...action.payload, contactPersons };
    },
    setContactLoading (state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setLabelLoading (state, action: PayloadAction<boolean>) {
      state.labelLoading = action.payload;
    },
    setLabelName (state, action: PayloadAction<{labelId:number, name: string}>) {
      state.labelList = (state.labelList as LabelList[]).map(
        label => label.id === action.payload.labelId ? { ...label, name: action.payload.name } : label
      );
    },
    setNewLabel (state, action: PayloadAction<LabelList>) {
      state.labelList.push(action.payload);
      state.labelList = [...state.labelList];
    },
    setDeleteLabel (state, action: PayloadAction<number>) {
      state.labelList = state.labelList.filter(l => l.id !== action.payload);
    }
  }
});

export default contact.reducer;

export const getUserInfo = createAction('contact/getUserInfo');
export const getCompanyList = createAction('contact/getCompanyList');
export const getContactList = createAction('contact/getContactList');
export const searchContactList = createAction<{ keyword: string, callback:(data: ContactV2) => void }>('contact/searchContactList');
export const getUnionContacts = createAction<{ callback:(data: ContactV2) => void }>('contact/getUnionContacts');
export const searchUnionContacts = createAction<{ keyword: string, callback:(data: ContactV2) => void }>('contact/searchUnionContacts');
export const getOrgContactList = createAction('contact/getOrgContactList');
export const getRecommendList = createAction('contact/getRecommendList');
export const getExternalContactList = createAction('contact/getExternalContactList');
export const getLabels = createAction('contact/getLabels');
export const getLabelList = createAction<number>('contact/getLabelList');
export const getStaffLabelList = createAction<string>('contact/getStaffLabelList');
export const getSearchList = createAction<{keyword: string, searchId?: string}>('contact/getSearchList');
export const editContact = createAction<{remark: string, staffId: string, type: string, labelId: number}>('contact/editContact');
export const addContact = createAction<{id: string, staffId: string, meetingId?: string, labelId?: number}>('contact/addContact');
export const deleteContact = createAction<{staffId: string, type: string, labelId: number}>('contact/deleteContact');
export const updateLabelMember = createAction<{labelId: number, persons: Person[], name: string}>('contact/updateLabelMember');
export const createLabel = createAction<{persons: Person[], name: string}>('contact/createLabel');
export const deleteLabel = createAction<number>('contact/deleteLabel');

export const {
  setContactList,
  setSearchContactList,
  setOrgContactList,
  setUnionContacts,
  setRecommendList,
  setExternalContactList,
  setLabels,
  setLabelList,
  setStaffLabelList,
  setSearchList,
  setContactLoading,
  setLabelLoading,
  setLabelName,
  setNewLabel,
  setDeleteLabel
} = contact.actions;
